import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  message,
  Spin,
  Space,
  Row,
  Col,
  Divider,
  Radio,
  Modal
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import UploadImage from '@/components/UploadImage';
import { PageLink } from '@/config/pageConfig';
import { useRequest } from 'ahooks';
import { getAllArtworkAiFilterStyle, getArtworkAiFilterStyleDetail, updateArtworkAiFilterStyle, postArtworkAiFilterStyleAdd, deleteArtworkAiFilterStyleByStyleType } from '@/api/filter';
import AddOption from './AddOption';
import { ArtworkAifilterStyleDTO } from '@/types/filter';
import { DeleteOutlined } from '@ant-design/icons';

interface ISelectOption {
  label: string;
  value: string;
}

const ConvertDetail: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [disableStyleTypeInputs, setDisableStyleTypeInputs] = useState(false);
  
  // 监听model字段变化
  const model = Form.useWatch('model', form);

  const [styleTypeOptions, setStyleTypeOptions] = useState<ArtworkAifilterStyleDTO[]>([]);

  useEffect(() => {
    if (id) {
      fetchDetail(Number(id));
    }
  }, [id]);

  const {
    data: styleTypeOptionsSource = [],
  } = useRequest(
    async () => {
      const resp = await getAllArtworkAiFilterStyle();
      if (resp.status !== 200) {
        return [];
      }
      return resp.data.data;
    }
  );

  useEffect(() => {
    setStyleTypeOptions(styleTypeOptionsSource);
  }, [styleTypeOptionsSource]);


  const {
    data = {},
    loading,
    run: fetchDetail
  } = useRequest(
    async (styleId: number) => {
      const resp = await getArtworkAiFilterStyleDetail({
        styleId
      });
      if (resp.status !== 200) {
        return [];
      }
      return resp.data.data;
    },
    {
      manual: true
    }
  );

  useEffect(() => {
    if ((data as ArtworkAifilterStyleDTO).styleId) {
      form.setFieldsValue(data);
    }

  }, [data]);

  const onOptionAdd = (option: ArtworkAifilterStyleDTO) => {
    setStyleTypeOptions((value) => {
      value.push(option)
      return [...value];
    })
  }

  const onDeleteStyleType = (e: React.MouseEvent<HTMLElement, MouseEvent>, option: ISelectOption) => {
    e.stopPropagation();
    Modal.confirm({
      content: '删除此标签及标签下风格，是否确认删除',
      async onOk() {
        const res = await deleteArtworkAiFilterStyleByStyleType({
          styleType: option.value
        })
        const data = res.data;
        if (data.status === 200) {
          setStyleTypeOptions((value) => {
            return value.filter(item => item.styleType !== option.value)
          })

          const styleType = form.getFieldValue("styleType");

          if (styleType === option.value) {
            navigate(PageLink.Convert);
          }
        }
      },
      onCancel() { },
    });
  }

  const onSubmit = async () => {
    const values = await form.validateFields();
    const payload: ArtworkAifilterStyleDTO = {
      ...values,
    };
    try {
      const res = isEdit
        ? await updateArtworkAiFilterStyle({ ...payload, styleId: Number(id) })
        : await postArtworkAiFilterStyleAdd(payload);

      if (res.data.success) {
        message.success(`${isEdit ? '编辑' : '新增'}成功`);
        navigate(PageLink.Convert);
      } else {
        message.error(res.data.message || '操作失败');
      }
    } catch {
      message.error('请求失败，请稍后重试');
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        labelCol={{ flex: '0 0 140px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign="left"
        colon={false}
        layout="horizontal"
        style={{ maxWidth: 900, margin: '0 auto' }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="styleType"
              label="标签"
              rules={[{ required: true, message: "请选择" }]}
            >
              <Select
                allowClear
                placeholder="请选择"
                popupRender={menu => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <AddOption onAdd={onOptionAdd} />
                  </>
                )}
                options={styleTypeOptions.map(item => ({ label: item.styleType, value: item.styleType }))}
                optionRender={option => (
                  <Space style={{ width: "100%", justifyContent: "space-between" }}>
                    <span>
                      {option.label}
                    </span>
                    <DeleteOutlined onClick={(e) => onDeleteStyleType(e, option as ISelectOption)} />
                  </Space>
                )}
                onChange={(val) => {
                  if (val) {
                    const matched = styleTypeOptions.find(
                      (item) => item.styleType === val
                    );
                    if (matched) {
                      form.setFieldsValue({
                        styleType: matched.styleType,
                        styleTypeJa: matched.styleTypeJa,
                        styleTypeEn: matched.styleTypeEn,
                        styleTypeSort: matched.styleTypeSort,
                      });
                      // setDisableStyleTypeInputs(true);
                    } else {
                      form.setFieldsValue({
                        styleTypeJa: undefined,
                        styleTypeEn: undefined,
                        styleTypeSort: undefined
                      });
                      // setDisableStyleTypeInputs(false);
                    }
                  } else {
                    setDisableStyleTypeInputs(false);

                    form.setFieldsValue({
                      styleTypeJa: undefined,
                      styleTypeEn: undefined,
                      styleTypeSort: undefined
                    });
                  }
                }}
                style={{ width: "100%" }}
              >
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="styleTypeSort"
              label="标签排序"
              rules={[{ required: true }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="数字越大越靠前"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="styleTypeEn"
              label="标签名称（英）"
              rules={[{ required: true }]}
            >
              <Input
                placeholder="请输入"
                disabled={disableStyleTypeInputs}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="styleTypeJa"
              label="标签名称（日）"
              rules={[{ required: true }]}
            >
              <Input
                placeholder="请输入"
                disabled={disableStyleTypeInputs}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="styleName"
              label="风格名称（英）"
              rules={[{ required: true, message: '请输入英文风格名' }]}
            >
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="styleNameJa"
              label="风格名称（日）"
              rules={[{ required: true, message: '请输入日文风格名' }]}
            >
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="styleOrder"
          label="风格排序"
          rules={[{ required: true }]}
        >
          <InputNumber style={{ width: '100%' }} placeholder="数字越大越靠前" />
        </Form.Item>

        <Form.Item
          name="styleImageNew"
          label="风格图片(转绘前)"
          rules={[{ required: true, message: '请上传风格图' }]}
        >
          <UploadImage
            width={800}
            height={400}
            maxSize={10}
            showSizeHint={false}
            onlyWebp
          />
        </Form.Item>
        <Form.Item
          name="styleImage"
          label="风格图片(转绘后)"
          rules={[{ required: true, message: '请上传风格图' }]}
        >
          <UploadImage
            width={800}
            height={400}
            maxSize={10}
            showSizeHint={false}
            onlyWebp
          />
        </Form.Item>

        <Form.Item
          name="workFlowJson"
          label="工作流json"
          rules={[{ required: true }]}
        >
          <Input.TextArea placeholder="请输入" autoSize={{ minRows: 3, maxRows: 10 }} />
        </Form.Item>

        <Form.Item
          name="model"
          label="使用模型"
          rules={[{ required: true, message: '请选择使用模型' }]}
        >
          <Select placeholder="请选择使用模型">
            <Select.Option value={5}>eas-X</Select.Option>
            <Select.Option value={7}>豆包3.0-seededit3.0</Select.Option>
            <Select.Option value={8}>seedream4.5（Liblib）</Select.Option>
            <Select.Option value={18}>Seedream 5.0（火山引擎）</Select.Option>
            <Select.Option value={19}>Seedream 4.5（火山引擎）</Select.Option>
            <Select.Option value={20}>SuppFlux（Runware）</Select.Option>
          </Select>
        </Form.Item>

        {/* 选择了使用模型后，显示英文和日文两个输入框 */}
        {model && (
          <>
            <Form.Item
              name="defaultPromptEn"
              label="默认提示词（英文）"
            >
              <Input.TextArea 
                placeholder="请输入默认提示词（英文）" 
                autoSize={{ minRows: 3, maxRows: 6 }} 
              />
            </Form.Item>
            <Form.Item
              name="defaultPromptJa"
              label="默认提示词（日文）"
            >
              <Input.TextArea 
                placeholder="请输入默认提示词（日文）" 
                autoSize={{ minRows: 3, maxRows: 6 }} 
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="status"
          label="是否上架"
          rules={[{ required: true, message: '请选择' }]}
        >
          <Radio.Group
            options={[
              { value: 1, label: "是" },
              { value: 0, label: "否" },
            ]}
          >
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="limitTime"
          label="期间限定"
          rules={[{ required: true, message: '请选择' }]}
        >
          <Radio.Group
            options={[
              { value: 1, label: "是" },
              { value: 0, label: "否" },
            ]}
          >
          </Radio.Group>

        </Form.Item>

        {/* E15 高级功能配置 */}
        <Form.Item name="supportInpaint" label="支持局部重绘" initialValue={0}>
          <Radio.Group>
            <Radio value={0}>否</Radio>
            <Radio value={1}>是</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="supportStep" label="支持细化" initialValue={0}>
          <Radio.Group>
            <Radio value={0}>否</Radio>
            <Radio value={1}>是</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="support4k" label="支持4K" initialValue={0}>
          <Radio.Group>
            <Radio value={0}>否</Radio>
            <Radio value={1}>是</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 5 }}>
          <Space>
            <Button type="primary" onClick={onSubmit}>
              提交
            </Button>
            <Button onClick={() => navigate(-1)}>取消</Button>
          </Space>
        </Form.Item>
      </Form>
    </Spin>
  );
};

export default ConvertDetail;
